'use client'

import React, { useCallback, useEffect, useState } from 'react'
import Image from 'next/image'
import useEmblaCarousel from 'embla-carousel-react'
import { EmblaCarouselType } from 'embla-carousel'
import { useThrottledCallback } from 'use-debounce'
import AnimatedSection from '@/components/AnimatedSection'
import styles from './styles.module.css'

interface Stage {
	number: string
	title: string
	description: string
}

const WhyChooseUs: React.FC = () => {
	const stages: Stage[] = [
		{
			number: '01',
			title: 'Штатные специлисты',
			description: 'Мы не размещаем вакансию, когда к нам приходит запрос. В резерве true.code — разработчики, которые уже прошли отбор, имеют опыт в отраслевых проектах и готовы подключиться к работе.'
		},
		{
			number: '02',
			title: 'Конфиденциальность',
			description: 'Работаем в соответствии с договором о неразглашении конфиденциальной информации. Все ваши данные, код, бизнес-процессы и другие рабочие материалы защищены коммерческой тайной.'
		},
		{
			number: '03',
			title: 'Контроль качества',
			description: 'Работа всех специалистов true.code отслеживаются ежедневно с нашей стороны. Нам важно, чтобы сроки выполнения были соблюдены, а качество работы оставалось стабильно на высшем уровне.'
		},
		{
			number: '04',
			title: 'Без потери темпа',
			description: 'Замена специалиста без потери темпа. Если специалист выбывает из проекта — мы оперативно подбираем замену из резерва. Процесс не останавливается в ожидании найма нового человека.'
		}
	]

	const [emblaRef, emblaApi] = useEmblaCarousel({
		dragFree: true,
		align: 'start'
	})

	const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
	const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

	const onSelect = useCallback((api: EmblaCarouselType) => {
		setPrevBtnDisabled(!api.canScrollPrev())
		setNextBtnDisabled(!api.canScrollNext())
	}, [])

	const onSelectThrottled = useThrottledCallback(onSelect, 100)

	useEffect(() => {
		if (!emblaApi) return

		onSelect(emblaApi)
		emblaApi.on('reInit', onSelect).on('select', onSelect)
	}, [emblaApi, onSelect, onSelectThrottled])

	const onPrevButtonClick = useCallback(() => {
		if (!emblaApi) return
		emblaApi.scrollPrev()
	}, [emblaApi])

	const onNextButtonClick = useCallback(() => {
		if (!emblaApi) return
		emblaApi.scrollNext()
	}, [emblaApi])

	return (
		<section className={styles.whyChooseUs}>
			<AnimatedSection animation='slideUp'>
				<div className={styles.subtitle}>Почему выбирают нас</div>
				<h2 className={styles.title} dangerouslySetInnerHTML={{ __html: '<span>Гарантии качества</span> кадрового резерва true.code' }} />
			</AnimatedSection>
			<AnimatedSection animation='slideUp'>
				<div className={styles.stagesSlider}>
					<div className={styles.stagesSliderContainer}>
						<div className={styles.embla}>
							<div className={styles.embla__viewport} ref={emblaRef}>
								<div className={styles.embla__container}>
									{stages.map((stage, index) => {
										const imagePaths = [
											'/images/outstaffing/whychooseus_1.svg',
											'/images/outstaffing/whychooseus_2.svg',
											'/images/outstaffing/whychooseus_3.svg',
											'/images/outstaffing/whychooseus_4.svg'
										]

										return (
											<div key={index} className={styles.embla__slide}>
												<div className={styles.stageCard}>
													<div className={styles.stageNumber}>{stage.number}</div>
													<div className={styles.stageImage}>
														<Image
															src={imagePaths[index]}
															alt={stage.title}
															width={310}
															height={200}
															className={styles.image}
														/>
													</div>
													<h3 className={styles.stageTitle}>{stage.title}</h3>
													<p className={styles.stageDescription}>{stage.description}</p>
												</div>
											</div>
										)
									})}
								</div>
							</div>

							{!prevBtnDisabled && (
								<button className={styles.embla__prev} onClick={onPrevButtonClick}>
									<Image
										src='/images/portfolio/lk-agro-b2b/slider-button-left.svg'
										alt='Previous'
										width={48}
										height={48}
										className={styles.navButtonIcon}
									/>
								</button>
							)}

							{!nextBtnDisabled && (
								<button className={styles.embla__next} onClick={onNextButtonClick}>
									<Image
										src='/images/portfolio/lk-agro-b2b/slider-button-right.svg'
										alt='Next'
										width={48}
										height={48}
										className={styles.navButtonIcon}
									/>
								</button>
							)}
						</div>
					</div>
				</div>
			</AnimatedSection>
		</section>
	)
}

WhyChooseUs.displayName = 'WhyChooseUs'

export default WhyChooseUs
